import React, { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, TouchableOpacity } from 'react-native';
import { GestureDetector } from 'react-native-gesture-handler';
import Animated from 'react-native-reanimated';

import InfoIcon from '@/assets/icons/info';
import { InnerBorder } from '@/components/InnerBorder';
import { Text } from '@/components/TextComponent';
import { Box, Image } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { appTheme } from '@/components/lib/theme';
import { RadialGradient } from '@/components/linear-gradient/RadialGradient';
import { Tag } from '@/feature/lobby/components/EventHeader';
import { useHover } from '@/feature/responsive-design/hooks/use-hover';
import { common, designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';

import { CasinoGame } from '../hooks/use-casino-games';
import { useStartCasinoGameUrl } from '../hooks/use-formatted-casino-game-url';
import { useTapShrinkAnimation } from '../hooks/use-tap-shrink-animation';
import { getCasinoGameTag, isJackpotGame } from '../utils/utils';
import { useCasinoGameInfoModal } from './CasinoGameInfoModalProvider';
import { JackpotTicker } from './JackpotTicker';

interface CasinoGameCardBaseProps {
    game: CasinoGame;
    cardHeight?: number;
    cardWidth?: number;
    imageHeight?: number;
    showTags?: boolean;
    showName?: boolean;
    customTag?: { label: string };
    customNameVariant?: string;
    overlay?: ReactNode;
    showInfoIcon?: boolean;
}

export const CasinoGameCardBase = ({
    game,
    cardHeight = 104,
    cardWidth = 104,
    imageHeight = 104,
    showTags = true,
    showName = false,
    customTag,
    customNameVariant,
    overlay,
    showInfoIcon = true,
}: CasinoGameCardBaseProps) => {
    const { t } = useTranslation(['common']);
    const { handleStartGame } = useStartCasinoGameUrl(game);
    const onPress = useCallback(() => {
        triggerHapticFeedback();
        handleStartGame();
    }, [handleStartGame]);
    const { showGameInfo } = useCasinoGameInfoModal();
    const { tapGesture, animatedStyle, pressHandlers } = useTapShrinkAnimation({ onPress });
    const { hovered, hoverHandlers } = useHover();

    const tag = showTags ? getCasinoGameTag(game, customTag?.label) : undefined;

    const imageUrl = game.thumbnail || game.logo;
    const hasImage = Boolean(imageUrl);

    const handleInfoPress = () => {
        showGameInfo(game);
    };

    const card = (
        <Box
            height={cardHeight}
            width={cardWidth}
            style={styles.container}
            borderRadius="r20"
            overflow="hidden"
            backgroundColor={ds.colorToken.background.neutral.subtler}
        >
            <InnerBorder
                borderRadius="r20"
                borderColor={ds.colorValue.border.strong}
                boxProps={{ style: common.zIndex1 }}
            />
            <Box style={styles.shadow} flex={1}>
                <Box style={[styles.imageContainer, { height: imageHeight }]}>
                    {hasImage ? (
                        <>
                            <Image source={{ uri: `https:${imageUrl}` }} style={styles.image} resizeMode="cover" />
                            <RadialGradient size={152} positions={[0, 1]} style={styles.gradient} />
                        </>
                    ) : (
                        <Box style={styles.placeholderImage} backgroundColor={ds.colorToken.background.neutral.subtle}>
                            <Text color={ds.colorToken.text.primary} variant="bodySmall" textAlign="center">
                                {t('no_image')}
                            </Text>
                        </Box>
                    )}
                </Box>
            </Box>

            {hovered ? <Box pointerEvents="none" style={[StyleSheet.absoluteFill, styles.hoverWash]} /> : null}

            {tag ? (
                <Box position="absolute" top={10} left={10} style={common.zIndex2}>
                    <Tag label={tag.label} color={tag.color} />
                </Box>
            ) : null}

            {isJackpotGame(game) ? (
                <JackpotTicker
                    imageUri={hasImage ? `https:${imageUrl}` : undefined}
                    cardWidth={cardWidth}
                    imageHeight={imageHeight}
                />
            ) : null}

            {showName ? (
                <Box position="absolute" bottom={12} left={12} right={showInfoIcon ? 36 : 12} style={common.zIndex1}>
                    <Text
                        color={ds.colorToken.text.primary}
                        variant={(customNameVariant as any) || 'bodySmall'}
                        letterSpacing={0}
                        fontWeight="bold"
                        numberOfLines={2}
                    >
                        {game.name}
                    </Text>
                </Box>
            ) : null}

            {overlay}
        </Box>
    );

    return (
        <Box position="relative" {...hoverHandlers}>
            {isWeb ? (
                <Pressable
                    {...pressHandlers}
                    onPress={onPress}
                    style={styles.pressable}
                    accessibilityRole="button"
                    accessibilityLabel={game.name}
                >
                    <Animated.View style={[animatedStyle, hovered ? styles.hoverShadow : null]}>{card}</Animated.View>
                </Pressable>
            ) : (
                <GestureDetector gesture={tapGesture}>
                    <Animated.View style={animatedStyle}>{card}</Animated.View>
                </GestureDetector>
            )}

            {showInfoIcon ? (
                <TouchableOpacity onPress={handleInfoPress} hitSlop={8} style={styles.infoIconTouchable}>
                    <Box padding="s2" borderRadius="r8">
                        <InfoIcon width={16} height={16} color={ds.colorValue.icon.primary} />
                    </Box>
                </TouchableOpacity>
            ) : null}
        </Box>
    );
};

const styles = StyleSheet.create({
    container: {
        borderCurve: 'continuous',
    },
    image: {
        width: '100%',
        height: '100%',
    },
    placeholderImage: {
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    shadow: {
        shadowColor: designSystem.colors.neutralBlack,
        shadowOpacity: 0.4,
        shadowRadius: 24,
        elevation: 1,
        shadowOffset: { width: 0, height: 0 },
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderRadius: 12,
        borderCurve: 'continuous',
    },
    imageContainer: {
        overflow: 'hidden',
        borderRadius: 12,
        borderCurve: 'continuous',
        flex: 1,
    },
    gradient: {
        position: 'absolute',
        top: -76,
        right: -76,
        width: 152,
        height: 152,
    },
    infoIconTouchable: {
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 10,
    },
    pressable: {
        borderRadius: appTheme.borderRadii.r20,
        borderCurve: 'continuous',
    },
    hoverWash: {
        backgroundColor: ds.colorValue.background.transparent.strong,
    },
    hoverShadow: {
        borderRadius: appTheme.borderRadii.r20,
        borderCurve: 'continuous',
        shadowColor: designSystem.colors.neutralBlack,
        shadowOffset: { width: 0, height: 32 },
        shadowOpacity: 0.4,
        shadowRadius: 64,
    },
});
